/** Chats MFE entry point. View gating combines the SHELL USER's role with a HOST-forced override (pure decision: resolve-chats-view.ts): mission-control users get both tabs, everyone else gets Alex only, and `data-initial-view="assistant"` forces Alex-only REGARDLESS of role. */

import { useState, useSyncExternalStore, type CSSProperties } from 'react';
import { ARIA_ROLES } from '@federa/components/common/aria-tokens';
import { SelectableButton } from '@federa/components/primitives/SelectableButton';
import { createMFEMount } from '@federa/components/common/create-mfe-mount';
import { globalKey } from '@federa/const/global-namespace';
import { subscribeToShell } from '@federa/components/common/federa-shell-reader';
import { typography } from '@federa/components/css-refs.typography';
import { spacing } from '@federa/components/css-refs.spacing';
import { borderRadius, borderWidth } from '@federa/components/css-refs.effects';
import { loadingMessages } from '@federa/utils/styles/content-tokens';
import { MFE_NAMES } from '@federa/const/mfe-names';
import { FULL_HEIGHT_STYLE } from '@federa/const/style-size-presets';
import { FLEX_CENTER_STYLE, FLEX_COLUMN_STYLE } from '@federa/const/style-flex-presets';
import { KIND_ASSISTANT_ONLY } from '@federa/const/spa-chats-kind-values';
import { VIEWER_SHELL_COLORS } from './chats-viewer-tokens.ts';
import { resolveChatsView } from './resolve-chats-view.ts';
import { App } from './App.tsx';
import { AlexTab } from './assistant/AlexTab.tsx';
import { FunctionExecutionView } from './FunctionExecutionView.tsx';

interface ShellUser {
	isLoggedIn?: boolean;
	rawUserId?: string;
	role?: string;
}

function shellUser(): ShellUser | null {
	// Brand-key read without the Window-widening double cast (components/common/federa-shell-reader.ts).
	const shell = Reflect.get(window, globalKey('Shell')) as { user?: ShellUser } | undefined;
	return shell?.user ?? null;
}
function getUserId(): string | null {
	const u = shellUser();
	return u?.isLoggedIn && u.rawUserId ? u.rawUserId : null;
}
function getRole(): string | null {
	return shellUser()?.role ?? null;
}

const noteStyle: CSSProperties = {
	...FULL_HEIGHT_STYLE,
	...FLEX_CENTER_STYLE,
	color: VIEWER_SHELL_COLORS.muted,
	fontSize: typography.fontSize.sm,
};

const shellStyle: CSSProperties = {
	...FLEX_COLUMN_STYLE,
	height: '100dvh',
	minHeight: 0,
	overflow: 'hidden',
};

// Host-owned box: fill the container rather than the viewport. `embed` and `fill` must stay SEPARATE -- `embed` is the
// DISMISSIBLE side panel. `maxHeight: 100dvh` is REQUIRED: a percentage height resolves to `auto` unless the parent chain is bounded.
const containedShellStyle: CSSProperties = { ...shellStyle, ...FULL_HEIGHT_STYLE, maxHeight: '100dvh' };

const tabBarStyle: CSSProperties = {
	display: 'flex',
	gap: spacing[2],
	padding: `${spacing[2]} ${spacing[3]}`,
	borderBottom: `${borderWidth.DEFAULT} solid ${VIEWER_SHELL_COLORS.border}`,
	backgroundColor: VIEWER_SHELL_COLORS.surface,
	flex: 'none',
};

function tabButtonStyle(active: boolean): CSSProperties {
	return {
		padding: `${spacing[2]} ${spacing[3]}`,
		borderRadius: borderRadius.full,
		border: `${borderWidth.DEFAULT} solid ` + (active ? VIEWER_SHELL_COLORS.inkStrong : VIEWER_SHELL_COLORS.border),
		backgroundColor: active ? VIEWER_SHELL_COLORS.inkStrong : 'transparent',
		color: active ? VIEWER_SHELL_COLORS.onAccent : VIEWER_SHELL_COLORS.ink,
		fontSize: typography.fontSize.sm,
		fontWeight: typography.fontWeight.semibold,
		cursor: 'pointer',
	};
}

const tabPaneStyle: CSSProperties = {
	flex: 1,
	minHeight: 0,
	...FLEX_COLUMN_STYLE,
	overflow: 'hidden',
};

type ChatsTab = 'conversations' | 'alex';

/** The tab bar's entries in render order; the tab pane still switches on the id explicitly. */
const CHATS_TABS: { id: ChatsTab; label: string }[] = [
	{ id: 'conversations', label: 'Conversations' },
	{ id: 'alex', label: 'Alex' },
];

function TabbedChats({ embedded, fillsContainer }: { embedded: boolean; fillsContainer: boolean }) {
	const [tab, setTab] = useState<ChatsTab>('conversations');
	return (
		<div style={fillsContainer ? containedShellStyle : shellStyle}>
			<div style={tabBarStyle} role={ARIA_ROLES.tablist} aria-label="Chats views">
				{/* `SelectableButton` rather than the general `Button`: this strip renders UPSTREAM of the viewer's own token
				    block, so its palette is the literal one in `chats-viewer-tokens.ts` and a variant's `var()`-based
				    hover repaint would have nothing to resolve. The selectable primitive styles nothing itself -- it owns
				    the selection ARIA and takes the caller's box. */}
				{CHATS_TABS.map((entry) => (
					<SelectableButton
						key={entry.id}
						role={ARIA_ROLES.tab}
						selectionAria="selected"
						isSelected={tab === entry.id}
						style={tabButtonStyle(tab === entry.id)}
						onSelect={() => setTab(entry.id)}
					>
						{entry.label}
					</SelectableButton>
				))}
			</div>
			<div style={tabPaneStyle}>{tab === 'conversations' ? <App /> : <AlexTab embedded={embedded} />}</div>
		</div>
	);
}

function ChatsApp({ embedded, fillsContainer, assistantOnly }: { embedded: boolean; fillsContainer: boolean; assistantOnly: boolean }) {
	const userId = useSyncExternalStore(subscribeToShell, getUserId);
	const role = useSyncExternalStore(subscribeToShell, getRole);
	if (!userId) return <div style={noteStyle}>{loadingMessages.default}</div>;
	if (resolveChatsView({ assistantOnly, role }).kind === KIND_ASSISTANT_ONLY) {
		return (
			<div style={fillsContainer ? containedShellStyle : shellStyle}>
				<AlexTab embedded={embedded} />
			</div>
		);
	}
	return <TabbedChats embedded={embedded} fillsContainer={fillsContainer} />;
}

// `data-view="function"` mounts the deep-link view; every other mount gets the chats app.
createMFEMount({
	name: MFE_NAMES.CHATS,
	renderApp: (container) =>
		container.dataset.view === 'function' ? (
			<FunctionExecutionView />
		) : (
			<ChatsApp
				embedded={container.dataset.layout === 'embed'}
				fillsContainer={container.dataset.layout === 'embed' || container.dataset.layout === 'fill'}
				assistantOnly={container.dataset.initialView === 'assistant'}
			/>
		),
});
